import { useSyncExternalStore } from 'react'
import type { AuthSession } from '../features/auth/types'
import { UserTypes } from '../types/authorization'

const STORAGE_KEY = 'di.auth.session'
const AUTHZ_CACHE_PREFIX = 'authz:'
const BRWFM_REAUTH_PLATFORM_STORAGE_KEY = 'platformId'

let currentSession: AuthSession | null = null
const listeners = new Set<() => void>()

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function normalizeUserTypeId(value: unknown): UserTypes | undefined {
  const parsed = normalizeNumber(value)
  return Object.values(UserTypes).includes(parsed as UserTypes) ? (parsed as UserTypes) : undefined
}

function normalizeSession(value: unknown): AuthSession | null {
  if (!value || typeof value !== 'object') return null
  const session = value as AuthSession & { CurrentUserTypeId?: unknown; currentUserTypeId?: unknown }
  return {
    ...session,
    currentUserTypeId: normalizeUserTypeId(session.currentUserTypeId ?? session.CurrentUserTypeId),
  }
}

function readFromStorage(): AuthSession | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return normalizeSession(JSON.parse(raw))
  } catch {
    return null
  }
}

function emit() {
  listeners.forEach((cb) => cb())
}

export function hydrateSession() {
  currentSession = readFromStorage()
}

export function getSession(): AuthSession | null {
  if (!currentSession) currentSession = readFromStorage()
  return currentSession
}

export function setSession(next: AuthSession) {
  currentSession = normalizeSession(next)
  localStorage.setItem(STORAGE_KEY, JSON.stringify(currentSession))
  emit()
}

export function clearSessionStorage() {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(BRWFM_REAUTH_PLATFORM_STORAGE_KEY)

  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i)
    if (!key?.startsWith(AUTHZ_CACHE_PREFIX)) continue
    localStorage.removeItem(key)
  }
}

export function clearSession() {
  currentSession = null
  clearSessionStorage()
  emit()
}

export function updateSession(partial: Partial<AuthSession>) {
  const base = getSession()
  if (!base) return
  setSession({ ...base, ...partial })
}

export function isTokenExpired(session: AuthSession | null = getSession()) {
  if (!session?.tokenExpiry) return true
  return new Date() >= new Date(session.tokenExpiry)
}

export function isAuthenticated() {
  const session = getSession()
  return !!session?.idToken && !isTokenExpired(session)
}

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function useAuthSession() {
  return useSyncExternalStore(subscribe, getSession, getSession)
}
